(function () {
  const TABLET_QUERY = window.matchMedia("(min-width: 768px) and (max-width: 1067px)");
  const DESKTOP_QUERY = window.matchMedia("(min-width: 1068px)");
  const MOBILE_VISIBLE = 1.84;
  const TABLET_VISIBLE = 2.5;
  const DESKTOP_VISIBLE = 4.97;
  const AUTOPLAY_MS_DEFAULT = 5000;
  const GAP_VAR = "--ig-feed-gap";
  const GAP_DEFAULT = 8;
  const HYDRATE_ATTEMPTS = 12;
  const GSC_RUNTIME_WAIT_ATTEMPTS = 40;
  const MAX_RETRIES = 60;
  const IG_MEDIA_SELECTOR =
    ".gfa-media, .gfa-carousel__slide-media, .gfa-horizontal-scroll__item-media";
  const DRAG_THRESHOLD = 8;

  function getGap(section) {
    const gap = parseFloat(getComputedStyle(section).getPropertyValue(GAP_VAR));
    return Number.isFinite(gap) ? gap : GAP_DEFAULT;
  }

  function getVisibleCount() {
    if (DESKTOP_QUERY.matches) return DESKTOP_VISIBLE;
    if (TABLET_QUERY.matches) return TABLET_VISIBLE;
    return MOBILE_VISIBLE;
  }

  function getAutoplayMs(section) {
    const fromDataset = parseInt(section?.dataset.igAutoplayMs, 10);
    return Number.isFinite(fromDataset) && fromDataset > 0 ? fromDataset : AUTOPLAY_MS_DEFAULT;
  }

  function getCarousel(section) {
    const viewport = section?.querySelector(".gfa-carousel__viewport");
    const track = viewport?.querySelector(".gfa-carousel__track");
    if (!viewport || !track) return null;

    const slides = getFeedItems(section).filter(
      (slide) => !slide.hidden && slide.dataset.igEmpty !== "true" && slide.offsetParent !== null && getComputedStyle(slide).display !== "none"
    );

    return { viewport, track, slides };
  }

  function getActiveSlideIndex(viewport, slides) {
    const scrollLeft = viewport.scrollLeft;
    let activeIndex = 0;
    let minDistance = Infinity;

    slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - scrollLeft);
      if (distance < minDistance) {
        minDistance = distance;
        activeIndex = index;
      }
    });

    return activeIndex;
  }

  function scrollToSlide(viewport, slide) {
    if (!viewport || !slide) return;
    viewport.scrollTo({ left: slide.offsetLeft, behavior: "smooth" });
  }

  function neutralizeTrackTransform(track) {
    if (!track || track.dataset.igNativeScroll === "true") return;
    track.dataset.igNativeScroll = "true";

    const pinTransform = () => {
      if (track.style.transform && track.style.transform !== "none") {
        track.style.setProperty("transform", "none", "important");
      }
      if (track.style.translate && track.style.translate !== "none") {
        track.style.setProperty("translate", "none", "important");
      }
    };

    pinTransform();
    new MutationObserver(pinTransform).observe(track, {
      attributes: true,
      attributeFilter: ["style"],
    });
  }

  function neutralizeViewportScroll(viewport) {
    if (!viewport || viewport.dataset.igScrollPinned === "true") return;
    viewport.dataset.igScrollPinned = "true";

    const pinScroll = () => {
      viewport.style.setProperty("overflow-x", "auto", "important");
      viewport.style.setProperty("overflow-y", "hidden", "important");
      viewport.style.setProperty("touch-action", "pan-x", "important");
    };

    pinScroll();
    new MutationObserver(pinScroll).observe(viewport, {
      attributes: true,
      attributeFilter: ["style"],
    });
  }

  function snapToNearestSlide(section) {
    const carousel = getCarousel(section);
    if (!carousel || !carousel.slides.length) return;

    const active = getActiveSlideIndex(carousel.viewport, carousel.slides);
    scrollToSlide(carousel.viewport, carousel.slides[active]);
  }

  function preventMediaDrag(section) {
    section.querySelectorAll(".gfa-media, .gfa-media img, .gfa-media video").forEach((node) => {
      node.setAttribute("draggable", "false");
    });
  }

  function bindDragScroll(section, viewport) {
    if (!viewport || viewport.dataset.igDragBound === "true") return;
    viewport.dataset.igDragBound = "true";

    let activePointerId = null;
    let activePointerType = "";
    let startX = 0;
    let scrollStart = 0;
    let scrollStartLeft = 0;
    let dragged = false;
    let suppressClick = false;

    const finishDrag = (event) => {
      if (activePointerId === null || (event && event.pointerId !== activePointerId)) return;

      const didDrag = dragged;
      activePointerId = null;
      activePointerType = "";
      dragged = false;
      viewport.classList.remove("is-dragging");
      viewport.style.scrollBehavior = "";

      if (didDrag) {
        suppressClick = true;
        snapToNearestSlide(section);
        window.setTimeout(() => {
          suppressClick = false;
        }, 300);
      }

      startAutoplay(section);
    };

    viewport.addEventListener(
      "pointerdown",
      (event) => {
        if (event.pointerType === "mouse" && event.button !== 0) return;

        activePointerId = event.pointerId;
        activePointerType = event.pointerType;
        startX = event.clientX;
        scrollStart = viewport.scrollLeft;
        scrollStartLeft = viewport.scrollLeft;
        dragged = false;
        stopAutoplay(section);

        if (event.pointerType === "mouse" && !event.target.closest(IG_MEDIA_SELECTOR)) {
          viewport.classList.add("is-dragging");
          viewport.style.scrollBehavior = "auto";

          try {
            viewport.setPointerCapture(event.pointerId);
          } catch (error) {
            /* noop */
          }
        }
      },
      { passive: true }
    );

    viewport.addEventListener(
      "pointermove",
      (event) => {
        if (activePointerId === null || event.pointerId !== activePointerId) return;

        const deltaX = event.clientX - startX;
        if (Math.abs(deltaX) < DRAG_THRESHOLD) return;

        if (activePointerType === "mouse") {
          dragged = true;
          event.preventDefault();
          viewport.scrollLeft = scrollStart - deltaX;
          return;
        }

        dragged = true;
      },
      { passive: false }
    );

    viewport.addEventListener("pointerup", finishDrag);
    viewport.addEventListener("pointercancel", finishDrag);
    viewport.addEventListener("lostpointercapture", finishDrag);

    viewport.addEventListener(
      "scroll",
      () => {
        if (activePointerId === null || activePointerType === "mouse") return;
        if (Math.abs(viewport.scrollLeft - scrollStartLeft) >= DRAG_THRESHOLD) {
          dragged = true;
        }
      },
      { passive: true }
    );

    viewport.addEventListener(
      "click",
      (event) => {
        if (!suppressClick) return;
        if (event.target.closest(IG_MEDIA_SELECTOR)) return;
        event.preventDefault();
        event.stopPropagation();
      }
    );

    viewport.addEventListener(
      "dragstart",
      (event) => {
        event.preventDefault();
      },
      true
    );
  }

  function resetCarouselStart(section) {
    const carousel = getCarousel(section);
    if (!carousel) return;
    carousel.viewport.scrollLeft = 0;
  }

  function stopAutoplay(section) {
    if (!section?._igAutoplayTimer) return;
    clearInterval(section._igAutoplayTimer);
    section._igAutoplayTimer = null;
  }

  function advanceCarousel(section) {
    const carousel = getCarousel(section);
    if (!carousel || carousel.slides.length <= 1) return;

    const { viewport, slides } = carousel;
    const maxScroll = Math.max(viewport.scrollWidth - viewport.clientWidth, 0);
    const tolerance = 2;

    if (viewport.scrollLeft >= maxScroll - tolerance) {
      viewport.scrollTo({ left: 0, behavior: "smooth" });
      return;
    }

    const active = getActiveSlideIndex(viewport, slides);
    const nextSlide = slides[active + 1];

    if (!nextSlide || nextSlide.offsetLeft > maxScroll + tolerance) {
      viewport.scrollTo({ left: 0, behavior: "smooth" });
      return;
    }

    scrollToSlide(viewport, nextSlide);
  }

  function startAutoplay(section) {
    if (!section) return;

    stopAutoplay(section);

    const carousel = getCarousel(section);
    if (!carousel || carousel.slides.length <= 1) return;

    section._igAutoplayTimer = setInterval(() => {
      advanceCarousel(section);
    }, getAutoplayMs(section));
  }

  function bindAutoplayPause(section) {
    if (section.dataset.igAutoplayBound === "true") return;
    section.dataset.igAutoplayBound = "true";

    const feed = section.querySelector(".app-wrapper__content");
    if (!feed) return;

    feed.addEventListener("mouseenter", () => stopAutoplay(section));
    feed.addEventListener("mouseleave", () => startAutoplay(section));
    feed.addEventListener("focusin", () => stopAutoplay(section));
    feed.addEventListener("focusout", () => startAutoplay(section));
  }

  function setupCarousel(section) {
    const carousel = getCarousel(section);
    if (!carousel || !carousel.slides.length) return false;

    neutralizeViewportScroll(carousel.viewport);
    neutralizeTrackTransform(carousel.track);
    preventMediaDrag(section);
    bindDragScroll(section, carousel.viewport);
    bindAutoplayPause(section);

    if (section.dataset.igCarouselAligned !== "true") {
      resetCarouselStart(section);
      section.dataset.igCarouselAligned = "true";
      startAutoplay(section);
    }

    return true;
  }

  function getItemLimit(section) {
    const limit = parseInt(section.dataset.igItemsLimit, 10);
    return Number.isFinite(limit) && limit > 0 ? limit : 10;
  }

  function getWidgetKey(section) {
    const fromDataset = section.dataset.igWidgetKey;
    if (fromDataset) return fromDataset;

    const widget = section.querySelector(".gfa-widget");
    if (!widget) return null;

    return Array.from(widget.classList).find((className) => className.startsWith("GSC-")) || null;
  }

  function getWidgetMedia(widgetKey) {
    const mediaStore = window.GSC_INSTAFEED_MEDIA;
    if (!Array.isArray(mediaStore)) return [];

    if (widgetKey) {
      const entry = mediaStore.find((item) => item && item.id === widgetKey);
      if (entry && Array.isArray(entry.data) && entry.data.length) {
        return entry.data;
      }
    }

    const firstEntry = mediaStore.find((item) => item && Array.isArray(item.data) && item.data.length);
    return firstEntry ? firstEntry.data : [];
  }

  function resolvePostMedia(post) {
    if (!post) return null;

    if (post.mediaType === "VIDEO" && post.mediaUrl) {
      return {
        type: "video",
        src: post.mediaUrl,
        poster: post.thumbnailUrl || post.mediaUrl,
      };
    }

    if (post.mediaType === "IMAGE" && post.mediaUrl) {
      return { type: "image", src: post.mediaUrl };
    }

    if (post.mediaType === "CAROUSEL_ALBUM" && Array.isArray(post.children) && post.children.length) {
      const videoChild = post.children.find((child) => child && child.mediaType === "VIDEO" && child.mediaUrl);
      if (videoChild) {
        return {
          type: "video",
          src: videoChild.mediaUrl,
          poster: videoChild.thumbnailUrl || post.thumbnailUrl || post.mediaUrl || "",
        };
      }

      const imageChild = post.children.find((child) => child && child.mediaUrl);
      if (imageChild) {
        return {
          type: imageChild.mediaType === "VIDEO" ? "video" : "image",
          src: imageChild.mediaUrl,
          poster: imageChild.thumbnailUrl || post.thumbnailUrl || post.mediaUrl || "",
        };
      }
    }

    if (post.mediaUrl) {
      return { type: "image", src: post.mediaUrl };
    }

    if (post.thumbnailUrl) {
      return { type: "image", src: post.thumbnailUrl };
    }

    return null;
  }

  function widgetHasFeed(widgetEl) {
    return Boolean(
      widgetEl &&
        widgetEl.querySelector(
          ".gfa-carousel__slide .gfa-media, .gfa-content__grid-item .gfa-media, .gsc-ig-fallback-feed .gfa-media"
        )
    );
  }

  function ensureWidgetElement(section, widgetKey) {
    let widgetEl =
      (widgetKey && section.querySelector(`.gfa-widget.${widgetKey}`)) ||
      section.querySelector(".gfa-widget");

    if (widgetEl) return widgetEl;

    const content = section.querySelector(".app-wrapper__content");
    if (!content) return null;

    widgetEl = document.createElement("div");
    widgetEl.className = "gfa-widget";
    if (widgetKey) widgetEl.classList.add(widgetKey);
    content.appendChild(widgetEl);
    return widgetEl;
  }

  function hydrateGscWidgets(section) {
    if (typeof window.GSC_INSTAFEED_RUNTIME !== "function") return false;
    if (!Array.isArray(window.GSC_INSTAFEED_WIDGETS)) return false;

    let mounted = false;

    window.GSC_INSTAFEED_WIDGETS.forEach((widgetConfig) => {
      const widgetKey = widgetConfig && widgetConfig.key;
      if (!widgetKey || widgetConfig.enabled === false) return;

      const media = getWidgetMedia(widgetKey);
      if (!media.length) return;

      section.querySelectorAll(`.gfa-widget.${widgetKey}`).forEach((widgetEl) => {
        const isFallbackFeed = Boolean(widgetEl.querySelector(".gsc-ig-fallback-feed"));
        if (widgetHasFeed(widgetEl) && !isFallbackFeed) return;

        if (isFallbackFeed) {
          widgetEl.innerHTML = "";
        }

        try {
          window.GSC_INSTAFEED_RUNTIME(widgetEl, {
            settings: widgetConfig,
            platform: "Runtime",
            blockId: widgetEl.id,
            media,
          });
          mounted = true;
        } catch (error) {
          console.warn("[gsc-instagram-home] hydrate failed", error);
        }
      });
    });

    return mounted;
  }

  function createMeta(handle, avatarUrl) {
    const meta = document.createElement("div");
    meta.className = "gsc-ig-item-meta";
    meta.setAttribute("aria-hidden", "true");

    if (avatarUrl) {
      const avatar = document.createElement("img");
      avatar.className = "gsc-ig-item-meta__avatar";
      avatar.src = avatarUrl;
      avatar.alt = "";
      avatar.width = 24;
      avatar.height = 24;
      avatar.loading = "lazy";
      avatar.decoding = "async";
      meta.appendChild(avatar);
    }

    const handleEl = document.createElement("span");
    handleEl.className = "gsc-ig-item-meta__handle";
    handleEl.textContent = handle;
    meta.appendChild(handleEl);

    return meta;
  }

  function gscRuntimePending() {
    return Boolean(
      document.querySelector('script[src*="gsc-instafeed-widget"]') &&
        typeof window.GSC_INSTAFEED_RUNTIME !== "function"
    );
  }

  function isThemeManagedFeed(section) {
    return Boolean(section.querySelector(".gsc-ig-fallback-feed"));
  }

  function getMediaSource(media) {
    if (!media) return "";
    const video = media.querySelector("video");
    if (video?.currentSrc || video?.src) return video.currentSrc || video.src;
    const image = media.querySelector("img:not(.gsc-ig-item-meta__avatar)");
    if (image?.currentSrc || image?.src) return image.currentSrc || image.src;
    return "";
  }

  function normalizeMediaUrl(url) {
    if (!url) return "";
    try {
      return new URL(url, window.location.origin).pathname.split("/").pop() || "";
    } catch (error) {
      return String(url).split("?")[0].split("/").pop() || "";
    }
  }

  function postMatchesSrc(post, src) {
    if (!post || !src) return false;
    const target = normalizeMediaUrl(src);
    const candidates = [post.mediaUrl, post.thumbnailUrl];
    if (Array.isArray(post.children)) {
      post.children.forEach((child) => {
        candidates.push(child?.mediaUrl, child?.thumbnailUrl);
      });
    }
    return candidates.some((candidate) => candidate && normalizeMediaUrl(candidate) === target);
  }

  function getVisibleSlides(section) {
    return [
      ...section.querySelectorAll(
        ".gfa-carousel__slide:not([hidden]), .gfa-content__grid-item:not([hidden]), .gfa-horizontal-scroll__item:not([hidden])"
      ),
    ].filter((slide) => slide.offsetParent !== null && getComputedStyle(slide).display !== "none");
  }

  function resolvePostForMedia(section, media) {
    const posts = getWidgetMedia(getWidgetKey(section));
    if (!posts.length) return null;

    const slide = media.closest(
      ".gfa-carousel__slide, .gfa-content__grid-item, .gfa-horizontal-scroll__item"
    );
    const src = getMediaSource(media);

    if (slide?.dataset.igPostIndex) {
      const index = parseInt(slide.dataset.igPostIndex, 10);
      if (Number.isFinite(index) && posts[index]) {
        return { post: posts[index], posts, index };
      }
    }

    if (src) {
      const matchedIndex = posts.findIndex((post) => postMatchesSrc(post, src));
      if (matchedIndex >= 0) {
        return { post: posts[matchedIndex], posts, index: matchedIndex };
      }
    }

    if (slide) {
      const slides = getVisibleSlides(section);
      const index = slides.indexOf(slide);
      if (index >= 0) {
        return { post: posts[index % posts.length], posts, index };
      }
    }

    return { post: posts[0], posts, index: 0 };
  }

  function getAlbumItems(post) {
    if (!post) return [];
    if (post.mediaType === "CAROUSEL_ALBUM" && Array.isArray(post.children) && post.children.length) {
      return post.children
        .map((child) => resolvePostMedia(child))
        .filter((item) => item && item.src);
    }
    const single = resolvePostMedia(post);
    return single && single.src ? [single] : [];
  }

  function formatCount(value) {
    const count = parseInt(value, 10);
    if (!Number.isFinite(count)) return "0";
    if (count >= 1000000) return `${(count / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1).replace(/\.0$/, "")}K`;
    return String(count);
  }

  function ensureIgPopup() {
    let popup = document.getElementById("gsc-ig-popup");
    if (popup) return popup;

    popup = document.createElement("div");
    popup.id = "gsc-ig-popup";
    popup.className = "gsc-ig-popup";
    popup.hidden = true;
    popup.innerHTML = `
      <div class="gsc-ig-popup__backdrop" data-ig-popup-close></div>
      <button type="button" class="gsc-ig-popup__screen-close" data-ig-popup-close aria-label="Close">×</button>
      <button type="button" class="gsc-ig-popup__feed-nav gsc-ig-popup__feed-nav--prev" data-ig-popup-prev aria-label="Previous post">‹</button>
      <button type="button" class="gsc-ig-popup__feed-nav gsc-ig-popup__feed-nav--next" data-ig-popup-next aria-label="Next post">›</button>
      <div class="gsc-ig-popup__card" role="dialog" aria-modal="true" aria-label="Instagram post">
        <header class="gsc-ig-popup__header">
          <div class="gsc-ig-popup__profile">
            <img class="gsc-ig-popup__avatar" alt="" width="32" height="32" hidden>
            <span class="gsc-ig-popup__username"></span>
          </div>
          <a class="gsc-ig-popup__view-post" href="#" target="_blank" rel="noopener noreferrer">View post</a>
        </header>
        <div class="gsc-ig-popup__media-stage">
          <button type="button" class="gsc-ig-popup__album-nav gsc-ig-popup__album-nav--prev" data-ig-album-prev aria-label="Previous image">‹</button>
          <div class="gsc-ig-popup__media"></div>
          <button type="button" class="gsc-ig-popup__album-nav gsc-ig-popup__album-nav--next" data-ig-album-next aria-label="Next image">›</button>
          <div class="gsc-ig-popup__dots" hidden></div>
        </div>
        <div class="gsc-ig-popup__engagement">
          <span class="gsc-ig-popup__likes" hidden></span>
          <span class="gsc-ig-popup__comments" hidden></span>
        </div>
        <p class="gsc-ig-popup__caption" hidden></p>
        <p class="gsc-ig-popup__credit">Run on GSC Instagram Feed</p>
      </div>
    `;
    document.body.appendChild(popup);

    popup.querySelectorAll("[data-ig-popup-close]").forEach((node) => {
      node.addEventListener("click", closeIgPopup);
    });
    popup.querySelector("[data-ig-popup-prev]")?.addEventListener("click", () => stepIgPopupPost(-1));
    popup.querySelector("[data-ig-popup-next]")?.addEventListener("click", () => stepIgPopupPost(1));
    popup.querySelector("[data-ig-album-prev]")?.addEventListener("click", () => stepIgPopupAlbum(-1));
    popup.querySelector("[data-ig-album-next]")?.addEventListener("click", () => stepIgPopupAlbum(1));

    document.addEventListener("keydown", (event) => {
      const el = document.getElementById("gsc-ig-popup");
      if (!el || el.hidden) return;
      if (event.key === "Escape") closeIgPopup();
      if (event.key === "ArrowLeft") stepIgPopupPost(-1);
      if (event.key === "ArrowRight") stepIgPopupPost(1);
    });

    return popup;
  }

  function closeIgPopup() {
    const popup = document.getElementById("gsc-ig-popup");
    if (!popup || popup.hidden) return;
    popup.querySelector(".gsc-ig-popup__media")?.querySelector("video")?.pause();
    popup.hidden = true;
    popup.setAttribute("hidden", "");
    popup.classList.remove("is-open");
    document.body.classList.remove("gsc-ig-popup-open");
  }

  function renderIgPopupMedia(mediaEl, item) {
    mediaEl.innerHTML = "";
    if (!item) return;

    if (item.type === "video") {
      const video = document.createElement("video");
      video.className = "gsc-ig-popup__source";
      video.src = item.src;
      if (item.poster) video.poster = item.poster;
      video.controls = true;
      video.playsInline = true;
      video.setAttribute("playsinline", "");
      video.preload = "metadata";
      mediaEl.appendChild(video);
      video.play()?.catch(() => {});
      return;
    }

    const image = document.createElement("img");
    image.className = "gsc-ig-popup__source";
    image.src = item.src;
    image.alt = "";
    mediaEl.appendChild(image);
  }

  function renderIgPopupAlbumDots(popup, albumItems, albumIndex) {
    const dots = popup.querySelector(".gsc-ig-popup__dots");
    if (!dots) return;

    if (albumItems.length <= 1) {
      dots.hidden = true;
      dots.innerHTML = "";
      return;
    }

    dots.hidden = false;
    dots.innerHTML = albumItems
      .map(
        (_, index) =>
          `<span class="gsc-ig-popup__dot${index === albumIndex ? " is-active" : ""}"></span>`
      )
      .join("");
  }

  function renderIgPopupPost(popup) {
    const posts = popup._igPosts || [];
    const postIndex = popup._igPostIndex || 0;
    const albumIndex = popup._igAlbumIndex || 0;
    const post = posts[postIndex];
    const section = popup._igSection;
    if (!post || !section) return;

    const albumItems = getAlbumItems(post);
    const mediaItem = albumItems[albumIndex] || albumItems[0];
    const handle = section.dataset.igHandle || post.username || "loco_monky_official";
    const avatarUrl = section.dataset.igAvatar || "";

    const avatar = popup.querySelector(".gsc-ig-popup__avatar");
    const username = popup.querySelector(".gsc-ig-popup__username");
    const viewPost = popup.querySelector(".gsc-ig-popup__view-post");
    const likes = popup.querySelector(".gsc-ig-popup__likes");
    const comments = popup.querySelector(".gsc-ig-popup__comments");
    const caption = popup.querySelector(".gsc-ig-popup__caption");
    const media = popup.querySelector(".gsc-ig-popup__media");
    const albumPrev = popup.querySelector(".gsc-ig-popup__album-nav--prev");
    const albumNext = popup.querySelector(".gsc-ig-popup__album-nav--next");
    const feedPrev = popup.querySelector(".gsc-ig-popup__feed-nav--prev");
    const feedNext = popup.querySelector(".gsc-ig-popup__feed-nav--next");

    username.textContent = handle.replace(/^@/, "");
    if (avatarUrl) {
      avatar.src = avatarUrl;
      avatar.hidden = false;
    } else {
      avatar.hidden = true;
    }

    if (post.permalink) {
      viewPost.href = post.permalink;
      viewPost.hidden = false;
    } else {
      viewPost.hidden = true;
    }

    renderIgPopupMedia(media, mediaItem);
    renderIgPopupAlbumDots(popup, albumItems, albumIndex);

    const likeCount = post.likeCount ?? post.likes ?? 0;
    const commentCount = post.commentsCount ?? post.comments ?? 0;
    likes.textContent = `♥ ${formatCount(likeCount)}`;
    comments.textContent = `💬 ${formatCount(commentCount)}`;
    likes.hidden = false;
    comments.hidden = false;

    const captionText = (post.caption || "").trim();
    if (captionText) {
      caption.textContent = captionText;
      caption.hidden = false;
    } else {
      caption.hidden = true;
    }

    const hasAlbumNav = albumItems.length > 1;
    albumPrev.hidden = !hasAlbumNav;
    albumNext.hidden = !hasAlbumNav;

    feedPrev.hidden = postIndex <= 0;
    feedNext.hidden = postIndex >= posts.length - 1;
  }

  function openIgPopup(section, posts, postIndex) {
    const popup = ensureIgPopup();
    popup._igSection = section;
    popup._igPosts = posts;
    popup._igPostIndex = postIndex;
    popup._igAlbumIndex = 0;
    renderIgPopupPost(popup);
    popup.removeAttribute("hidden");
    popup.hidden = false;
    popup.classList.add("is-open");
    document.body.classList.add("gsc-ig-popup-open");
  }

  function stepIgPopupPost(delta) {
    const popup = document.getElementById("gsc-ig-popup");
    if (!popup || popup.hidden || !popup._igPosts) return;
    const next = (popup._igPostIndex || 0) + delta;
    if (next < 0 || next >= popup._igPosts.length) return;
    popup._igPostIndex = next;
    popup._igAlbumIndex = 0;
    renderIgPopupPost(popup);
  }

  function stepIgPopupAlbum(delta) {
    const popup = document.getElementById("gsc-ig-popup");
    if (!popup || popup.hidden || !popup._igPosts) return;
    const post = popup._igPosts[popup._igPostIndex || 0];
    const albumItems = getAlbumItems(post);
    if (albumItems.length <= 1) return;
    const next = (popup._igAlbumIndex || 0) + delta;
    if (next < 0 || next >= albumItems.length) return;
    popup._igAlbumIndex = next;
    renderIgPopupPost(popup);
  }

  function activateIgMedia(section, media, event) {
    const now = performance.now();
    if (media._igLastActivated && now - media._igLastActivated < 400) return false;

    const resolved = resolvePostForMedia(section, media);
    if (!resolved) return false;

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    media._igLastActivated = now;
    openIgPopup(section, resolved.posts, resolved.index);
    return true;
  }

  function bindPopupTriggers(section) {
    if (section.dataset.igPopupBound === "true") return;
    section.dataset.igPopupBound = "true";

    let activeTap = null;

    section.addEventListener("pointerdown", (event) => {
      const media = event.target.closest(IG_MEDIA_SELECTOR);
      if (!media || !section.contains(media)) return;
      activeTap = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        media,
      };
    });

    section.addEventListener("pointerup", (event) => {
      if (!activeTap || event.pointerId !== activeTap.pointerId) return;
      const delta = Math.hypot(event.clientX - activeTap.startX, event.clientY - activeTap.startY);
      const media = activeTap.media;
      activeTap = null;
      if (delta > 12) return;
      activateIgMedia(section, media, event);
    });

    section.addEventListener("pointercancel", () => {
      activeTap = null;
    });

    section.addEventListener("click", (event) => {
      const media = event.target.closest(IG_MEDIA_SELECTOR);
      if (!media || !section.contains(media)) return;
      activateIgMedia(section, media, event);
    });
  }

  function createSlide(post, handle, avatarUrl, index, section) {
    const resolved = resolvePostMedia(post);
    if (!resolved || !resolved.src) return null;

    const slide = document.createElement("div");
    slide.className = "gfa-carousel__slide";
    slide.dataset.igPostIndex = String(index);

    const trigger = document.createElement("button");
    trigger.type = "button";
    trigger.className = "gfa-media";
    trigger.setAttribute("draggable", "false");
    trigger.setAttribute("aria-label", "View Instagram post");

    if (resolved.type === "video") {
      const video = document.createElement("video");
      video.className = "gfa-media__source";
      video.src = resolved.src;
      if (resolved.poster) video.poster = resolved.poster;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute("playsinline", "");
      video.setAttribute("webkit-playsinline", "");
      // Sin autoplay: primeVideos() reproduce/pausa según visibilidad para no descargar todos los vídeos.
      video.preload = resolved.poster ? "none" : "metadata";
      trigger.appendChild(video);
    } else {
      const image = document.createElement("img");
      image.className = "gfa-media__source";
      image.src = resolved.src;
      image.alt = "";
      image.loading = "lazy";
      image.decoding = "async";
      trigger.appendChild(image);
    }

    trigger.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      const posts = getWidgetMedia(getWidgetKey(section));
      const idx = parseInt(slide.dataset.igPostIndex, 10) || 0;
      if (posts.length) openIgPopup(section, posts, idx);
    });

    trigger.appendChild(createMeta(handle, avatarUrl));
    slide.appendChild(trigger);
    return slide;
  }

  function renderFallbackFeed(section) {
    if (typeof window.GSC_INSTAFEED_RUNTIME === "function") {
      return hydrateGscWidgets(section);
    }

    const widgetKey = getWidgetKey(section);
    const media = getWidgetMedia(widgetKey);
    if (!media.length) return false;

    const widgetEl = ensureWidgetElement(section, widgetKey);
    if (!widgetEl) return false;
    if (widgetHasFeed(widgetEl)) return true;

    const limit = getItemLimit(section);
    const handle = section.dataset.igHandle || "loco_monky_official";
    const avatarUrl = section.dataset.igAvatar || "";

    const feed = document.createElement("div");
    feed.className = "gsc-ig-fallback-feed gfa-carousel gfa-content";

    const viewport = document.createElement("div");
    viewport.className = "gfa-carousel__viewport";

    const track = document.createElement("div");
    track.className = "gfa-carousel__track";

    let rendered = 0;
    media.forEach((post) => {
      if (rendered >= limit) return;
      const slide = createSlide(post, handle, avatarUrl, rendered, section);
      if (!slide) return;
      track.appendChild(slide);
      rendered += 1;
    });

    if (!rendered) return false;

    viewport.appendChild(track);
    feed.appendChild(viewport);
    widgetEl.innerHTML = "";
    widgetEl.appendChild(feed);

    section.dataset.igFallbackBuilt = "true";
    return true;
  }

  function ensureFeed(section, hydrateAttempts) {
    if (hasFeedItems(section) && section.dataset.igFallbackBuilt !== "true") return true;

    if (hydrateAttempts < HYDRATE_ATTEMPTS) {
      hydrateGscWidgets(section);
      if (hasFeedItems(section) && section.dataset.igFallbackBuilt !== "true") return true;
    }

    if (gscRuntimePending() && hydrateAttempts < GSC_RUNTIME_WAIT_ATTEMPTS) {
      return false;
    }

    if (typeof window.GSC_INSTAFEED_RUNTIME === "function") {
      return hydrateGscWidgets(section) || hasFeedItems(section);
    }

    return renderFallbackFeed(section) || hasFeedItems(section);
  }

  function limitItems(section) {
    const limit = getItemLimit(section);
    const items = section.querySelectorAll(".gfa-carousel__slide, .gfa-content__grid-item");
    if (!items.length) return;

    items.forEach((item, index) => {
      const hidden = index >= limit;
      item.hidden = hidden;
      if (hidden) {
        item.style.setProperty("display", "none", "important");
        item.setAttribute("aria-hidden", "true");
      } else {
        item.style.removeProperty("display");
        item.removeAttribute("aria-hidden");
      }
    });
  }

  function applyItemOverlays(section) {
    const handle = section.dataset.igHandle || "loco_monky_official";
    const avatarUrl = section.dataset.igAvatar || "";

    section.querySelectorAll(".gfa-media").forEach((media) => {
      if (media.querySelector(".gsc-ig-item-meta")) return;
      media.appendChild(createMeta(handle, avatarUrl));
    });

    section.querySelectorAll(".gfa-media__toolbar").forEach((toolbar) => {
      toolbar.style.setProperty("display", "none", "important");
    });
  }

  function playVideoSilently(video) {
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => {});
    }
  }

  // Reproduce cada vídeo del feed solo mientras está (casi) en pantalla y lo pausa al salir,
  // en lugar de reproducir y descargar todos a la vez al montar la sección.
  const videoVisibilityObserver =
    "IntersectionObserver" in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target;
              if (entry.isIntersecting) {
                if (video.preload === "none") video.preload = "metadata";
                playVideoSilently(video);
              } else if (!video.paused) {
                video.pause();
              }
            });
          },
          { rootMargin: "150px 0px", threshold: 0.1 }
        )
      : null;

  function primeVideos(section) {
    section.querySelectorAll(".gfa-media video").forEach((video) => {
      video.muted = true;
      video.playsInline = true;
      video.setAttribute("playsinline", "");
      video.setAttribute("webkit-playsinline", "");

      if (video.dataset.gscIgPrimed === "true") return;
      video.dataset.gscIgPrimed = "true";

      if (video.hasAttribute("autoplay")) {
        video.removeAttribute("autoplay");
        video.autoplay = false;
      }

      if (videoVisibilityObserver) {
        videoVisibilityObserver.observe(video);
      } else {
        playVideoSilently(video);
      }
    });
  }

  function applySlideWidths(section) {
    const viewport = section.querySelector(".gfa-carousel__viewport");
    if (!viewport) return false;

    const slides = getFeedItems(section).filter(
      (slide) => !slide.hidden && slide.dataset.igEmpty !== "true"
    );
    if (!slides.length) return false;

    const viewportWidth = viewport.getBoundingClientRect().width;
    if (!viewportWidth) return false;

    const gap = getGap(section);
    const visible = getVisibleCount();
    const cellWidth = Math.max((viewportWidth - gap * 0.5) / visible, 0);
    const widthValue = `${cellWidth}px`;

    slides.forEach((slide) => {
      slide.style.setProperty("width", widthValue, "important");
      slide.style.setProperty("min-width", widthValue, "important");
      slide.style.setProperty("max-width", widthValue, "important");
      slide.style.setProperty("flex", `0 0 ${widthValue}`, "important");
    });

    return true;
  }

  function hasFeedItems(section) {
    const items = getFeedItems(section);

    for (const item of items) {
      if (item.hidden || item.dataset.igEmpty === "true") continue;
      const media = getFeedMediaElement(item);
      if (hasValidFeedMedia(media)) return true;
    }

    return false;
  }

  function getFeedMediaElement(slide) {
    return (
      slide?.querySelector(
        ".gfa-media, .gfa-carousel__slide-media, .gfa-horizontal-scroll__item-media"
      ) || slide
    );
  }

  function normalizeFeedMediaUrl(value) {
    if (!value || typeof value !== "string") return "";
    const trimmed = value.trim();
    if (!trimmed || trimmed === "#" || trimmed === "about:blank") return "";

    try {
      const parsed = new URL(trimmed, window.location.origin);
      if (parsed.pathname === "/" && !parsed.search && !parsed.hash) return "";
      return trimmed;
    } catch (error) {
      return trimmed.length > 4 ? trimmed : "";
    }
  }

  function getFeedItems(section) {
    const explicit = [
      ...section.querySelectorAll(
        ".gfa-carousel__slide, .gfa-content__grid-item, .gfa-horizontal-scroll__item"
      ),
    ];
    if (explicit.length) return explicit;

    const track = section.querySelector(".gfa-carousel__track");
    if (!track) return [];

    return [...track.children].filter((child) => child.nodeType === 1);
  }

  function isInstagramDataResolved() {
    return (
      typeof window.GSC_INSTAFEED_RUNTIME === "function" &&
      Array.isArray(window.GSC_INSTAFEED_MEDIA)
    );
  }

  function hasWidgetFeedData(section) {
    const widgetKey = getWidgetKey(section);
    const media = getWidgetMedia(widgetKey);
    return media.some((post) => Boolean(resolvePostMedia(post)?.src));
  }

  function collapseInstagramFeed(section) {
    const feedContent =
      section.querySelector(".gsc-instagram-home__shell > .app-wrapper__content") ||
      section.querySelector(".app-wrapper__content");
    const carousel = section.querySelector(".gfa-carousel, .gsc-ig-fallback-feed");
    const widget = section.querySelector(".gfa-widget");

    section.classList.add("is-ig-feed-empty");
    feedContent?.classList.add("is-ig-feed-empty");
    carousel?.classList.add("is-ig-feed-empty");
    widget?.classList.add("is-ig-feed-empty");
  }

  function isRenderableFeedImage(image) {
    if (!image || image.classList.contains("gsc-ig-item-meta__avatar")) return false;
    if (image.dataset.igMediaFailed === "true") return false;

    const src =
      normalizeFeedMediaUrl(image.currentSrc) ||
      normalizeFeedMediaUrl(image.src) ||
      normalizeFeedMediaUrl(image.getAttribute("src"));
    if (!src) return false;

    if (image.complete) {
      return image.naturalWidth > 0 && image.naturalHeight > 0;
    }

    return false;
  }

  function isRenderableFeedVideo(video) {
    if (!video || video.dataset.igMediaFailed === "true") return false;

    let src =
      normalizeFeedMediaUrl(video.currentSrc) ||
      normalizeFeedMediaUrl(video.src) ||
      normalizeFeedMediaUrl(video.getAttribute("src"));

    if (!src) {
      for (const source of video.querySelectorAll("source")) {
        src =
          normalizeFeedMediaUrl(source.src) ||
          normalizeFeedMediaUrl(source.getAttribute("src"));
        if (src) break;
      }
    }

    if (!src) return false;
    if (video.error) return false;

    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      return video.videoWidth > 0 && video.videoHeight > 0;
    }

    return false;
  }

  function hasValidFeedMedia(mediaEl) {
    if (!mediaEl) return false;
    if (mediaEl.dataset.igMediaFailed === "true") return false;

    const video = mediaEl.querySelector("video");
    if (video) {
      return isRenderableFeedVideo(video);
    }

    const image = mediaEl.querySelector("img:not(.gsc-ig-item-meta__avatar)");
    if (image) {
      return isRenderableFeedImage(image);
    }

    const iframe = mediaEl.querySelector("iframe");
    if (iframe && normalizeFeedMediaUrl(iframe.src)) return true;

    const pictureSource = mediaEl.querySelector("picture source");
    if (pictureSource) {
      const srcset = pictureSource.srcset || pictureSource.getAttribute("srcset") || "";
      if (normalizeFeedMediaUrl(srcset.split(",")[0]?.trim().split(/\s+/)[0])) return true;
    }

    const backgroundImage = getComputedStyle(mediaEl).backgroundImage;
    if (backgroundImage && backgroundImage !== "none" && !/gradient/i.test(backgroundImage)) {
      const match = backgroundImage.match(/url\((['"]?)(.+?)\1\)/i);
      if (match && normalizeFeedMediaUrl(match[2])) return true;
    }

    return false;
  }

  function hideFeedItem(item) {
    item.hidden = true;
    item.style.setProperty("display", "none", "important");
    item.style.setProperty("width", "0", "important");
    item.style.setProperty("min-width", "0", "important");
    item.style.setProperty("max-width", "0", "important");
    item.style.setProperty("flex", "0 0 0", "important");
    item.style.setProperty("margin", "0", "important");
    item.style.setProperty("padding", "0", "important");
    item.style.setProperty("overflow", "hidden", "important");
    item.setAttribute("aria-hidden", "true");
    item.dataset.igEmpty = "true";

    const media = getFeedMediaElement(item);
    if (media && media !== item) {
      media.hidden = true;
      media.style.setProperty("display", "none", "important");
      media.dataset.igEmpty = "true";
    }
  }

  function showFeedItem(item) {
    if (item.dataset.igEmpty !== "true") return;

    item.hidden = false;
    item.style.removeProperty("display");
    item.style.removeProperty("width");
    item.style.removeProperty("min-width");
    item.style.removeProperty("max-width");
    item.style.removeProperty("flex");
    item.style.removeProperty("margin");
    item.style.removeProperty("padding");
    item.style.removeProperty("overflow");
    item.removeAttribute("aria-hidden");
    delete item.dataset.igEmpty;

    const media = getFeedMediaElement(item);
    if (media && media !== item) {
      media.hidden = false;
      media.style.removeProperty("display");
      delete media.dataset.igEmpty;
    }
  }

  function markBrokenFeedMedia(section) {
    section
      .querySelectorAll(".gfa-media img:not(.gsc-ig-item-meta__avatar), .gfa-carousel__slide-media img, .gfa-media video, .gfa-carousel__slide-media video")
      .forEach((node) => {
        if (node instanceof HTMLImageElement && node.complete && node.naturalWidth === 0) {
          node.dataset.igMediaFailed = "true";
        }

        if (node instanceof HTMLMediaElement && node.error) {
          node.dataset.igMediaFailed = "true";
        }
      });
  }

  function hideEmptyFeedItems(section) {
    markBrokenFeedMedia(section);

    if (isInstagramDataResolved() && !hasWidgetFeedData(section)) {
      getFeedItems(section).forEach((item) => hideFeedItem(item));
      collapseInstagramFeed(section);
      return 0;
    }

    const items = getFeedItems(section);
    let visibleCount = 0;

    items.forEach((item) => {
      const media = getFeedMediaElement(item);
      const hasContent = hasValidFeedMedia(media);

      if (!hasContent) {
        hideFeedItem(item);
        return;
      }

      showFeedItem(item);
      visibleCount += 1;
    });

    const feedContent =
      section.querySelector(".gsc-instagram-home__shell > .app-wrapper__content") ||
      section.querySelector(".app-wrapper__content");
    const carousel = section.querySelector(".gfa-carousel, .gsc-ig-fallback-feed");
    const widget = section.querySelector(".gfa-widget");
    const isEmpty = visibleCount === 0;

    section.classList.toggle("is-ig-feed-empty", isEmpty);
    feedContent?.classList.toggle("is-ig-feed-empty", isEmpty);
    carousel?.classList.toggle("is-ig-feed-empty", isEmpty);
    widget?.classList.toggle("is-ig-feed-empty", isEmpty);

    if (isEmpty) {
      section.querySelector(".gfa-carousel__track")?.style.setProperty("min-height", "0", "important");
    } else {
      section.querySelector(".gfa-carousel__track")?.style.removeProperty("min-height");
    }

    return visibleCount;
  }

  function bindEmptyFeedMediaWatchers(section) {
    if (section.dataset.igEmptyWatchBound === "true") return;
    section.dataset.igEmptyWatchBound = "true";

    const recheck = () => {
      hideEmptyFeedItems(section);
      if (hasFeedItems(section)) {
        applySlideWidths(section);
        setupCarousel(section);
      } else {
        stopAutoplay(section);
      }
    };

    section.addEventListener(
      "error",
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLImageElement || target instanceof HTMLMediaElement)) return;
        if (!section.contains(target)) return;
        if (target.classList.contains("gsc-ig-item-meta__avatar")) return;
        target.dataset.igMediaFailed = "true";
        recheck();
      },
      true
    );

    section.addEventListener(
      "loadedmetadata",
      (event) => {
        if (!(event.target instanceof HTMLMediaElement)) return;
        if (!section.contains(event.target)) return;
        if (event.target.videoWidth > 0 && event.target.videoHeight > 0) {
          delete event.target.dataset.igMediaFailed;
        } else {
          event.target.dataset.igMediaFailed = "true";
        }
        recheck();
      },
      true
    );

    section.addEventListener(
      "loadeddata",
      (event) => {
        if (!(event.target instanceof HTMLMediaElement)) return;
        if (!section.contains(event.target)) return;
        if (event.target.videoWidth > 0 && event.target.videoHeight > 0) {
          delete event.target.dataset.igMediaFailed;
        } else {
          event.target.dataset.igMediaFailed = "true";
        }
        recheck();
      },
      true
    );

    section.addEventListener(
      "load",
      (event) => {
        if (!(event.target instanceof HTMLImageElement)) return;
        if (!section.contains(event.target)) return;
        if (event.target.classList.contains("gsc-ig-item-meta__avatar")) return;
        if (event.target.naturalWidth > 0 && event.target.naturalHeight > 0) {
          delete event.target.dataset.igMediaFailed;
        } else {
          event.target.dataset.igMediaFailed = "true";
        }
        recheck();
      },
      true
    );
  }

  function bindSection(section) {
    if (!section || section.dataset.igPeekBound === "true") return;
    section.dataset.igPeekBound = "true";
    bindPopupTriggers(section);
    bindEmptyFeedMediaWatchers(section);

    let frame = null;
    let retryCount = 0;
    let hydrateAttempts = 0;

    const run = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!hasFeedItems(section)) {
          ensureFeed(section, hydrateAttempts);
          hydrateAttempts += 1;
        } else if (
          section.dataset.igFallbackBuilt === "true" &&
          typeof window.GSC_INSTAFEED_RUNTIME === "function"
        ) {
          const widgetKey = getWidgetKey(section);
          const widgetEl = ensureWidgetElement(section, widgetKey);
          if (widgetEl) {
            widgetEl.innerHTML = "";
            delete section.dataset.igFallbackBuilt;
            hydrateGscWidgets(section);
          }
        }

        if (
          section.querySelector(
            ".gfa-carousel__slide, .gfa-content__grid-item, .gfa-horizontal-scroll__item, .gfa-carousel__track"
          )
        ) {
          hideEmptyFeedItems(section);
        }

        if (hasFeedItems(section)) {
          applyItemOverlays(section);
          preventMediaDrag(section);
          primeVideos(section);

          const sized = applySlideWidths(section);
          if (sized) {
            setupCarousel(section);
          }
        } else if (isInstagramDataResolved() && !hasWidgetFeedData(section)) {
          stopAutoplay(section);
        }

        if (isThemeManagedFeed(section)) {
          limitItems(section);
        }

        let needsRetry = !hasFeedItems(section) && (gscRuntimePending() || hasWidgetFeedData(section));
        if (hasFeedItems(section)) {
          needsRetry = !applySlideWidths(section);
        }

        if (needsRetry && retryCount < MAX_RETRIES) {
          retryCount += 1;
          window.setTimeout(run, 250);
        }
      });
    };

    run();

    const content = section.querySelector(".app-wrapper__content") || section;
    const observer = new MutationObserver(() => {
      retryCount = 0;
      run();
    });
    observer.observe(content, { childList: true, subtree: true });

    if (window.ResizeObserver) {
      const feed =
        section.querySelector(".gsc-instagram-home__shell > .app-wrapper__content") || content;
      const resizeObserver = new ResizeObserver(() => {
        retryCount = 0;
        run();
      });
      resizeObserver.observe(feed);

      const viewport = section.querySelector(".gfa-carousel__viewport");
      if (viewport) resizeObserver.observe(viewport);
    }

    const onBreakpointChange = () => {
      section.dataset.igCarouselAligned = "false";
      retryCount = 0;
      stopAutoplay(section);
      run();
    };

    TABLET_QUERY.addEventListener("change", onBreakpointChange);
    DESKTOP_QUERY.addEventListener("change", onBreakpointChange);
    window.addEventListener(
      "resize",
      () => {
        retryCount = 0;
        run();
      },
      { passive: true }
    );

    window.setTimeout(run, 500);
    window.setTimeout(run, 1500);
    window.setTimeout(run, 3000);
  }

  function mountNow(section) {
    if (!section) return;
    bindSection(section);
  }

  // Montaje diferido: la sección está al final de la home, así que no se construye el feed
  // (ni se piden sus imágenes/vídeos) hasta que el usuario se acerca a ella.
  const sectionMountObserver =
    "IntersectionObserver" in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              sectionMountObserver.unobserve(entry.target);
              mountNow(entry.target);
            });
          },
          { rootMargin: "600px 0px" }
        )
      : null;

  function mount(section) {
    if (!section) return;
    if (section.dataset.igPeekBound === "true") return;
    if (!sectionMountObserver || window.Shopify?.designMode) {
      mountNow(section);
      return;
    }
    if (section.dataset.igMountObserved === "true") return;
    section.dataset.igMountObserved = "true";
    sectionMountObserver.observe(section);
  }

  function init(root) {
    ensureIgPopup();
    const scope = root || document;
    scope.querySelectorAll(".app-wrapper.gsc-instagram-home, [data-ig-widget-key]").forEach(mount);
  }

  window.gscInstagramHomeMount = mount;
  window.gscInstagramHomeInit = init;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init());
  } else {
    init();
  }

  window.addEventListener("load", () => init());

  document.addEventListener("shopify:section:load", (event) => {
    init(event.target);
  });
})();
